/**
 * Copy for the feature pages. Every statement here should match what the
 * product does today; the user guide in apps/docs is the reference.
 */

export interface FeatureBlock {
  title: string;
  body: string;
  points: string[];
}

export interface Feature {
  slug: string;
  name: string;
  title: string;
  lede: string;
  description: string;
  blocks: FeatureBlock[];
  /** Things the feature does not do yet. */
  limits: string[];
  docsPath: string;
  plan: string;
}

export const FEATURES: Feature[] = [
  {
    slug: "sites-drillholes",
    name: "Sites & drillholes",
    title: "Know the ground first.",
    lede: "A site carries its boundary, coordinate system and rock properties. Drillholes, topography, block models and cost model scenarios all hang off it.",
    description:
      "Manage open-pit sites, drillhole data and topography in MineOptics: CSV import, minimum-curvature traces, a 3D site view and data health checks.",
    blocks: [
      {
        title: "Sites",
        body: "Draw the boundary on the map or type its corner coordinates. A projected coordinate system is chosen from the site's position and can be changed afterwards.",
        points: ["EPSG code, vertical datum and units", "Rock density, UCS and hardness, from a preset or your own values", "Satellite imagery per site"],
      },
      {
        title: "Drillholes",
        body: "Create holes one at a time or import collars, surveys, geology and samples from CSV. Common column names are recognised, and rows with a missing or non-numeric value are reported instead of stored.",
        points: ["Hole IDs unique within a project", "Search and filter across the project", "Deleted holes are hidden, not erased"],
      },
      {
        title: "Traces and the 3D view",
        body: "Each hole's path is calculated from its collar and downhole surveys with the minimum curvature method, at one metre intervals, and drawn over the site's terrain.",
        points: ["Topography as a 3D surface", "Layers and traces can be shown or hidden", "Terrain display modes and a time-of-day light"],
      },
      {
        title: "Topography",
        body: "Upload survey points as easting, northing and elevation, or fill a layer by sampling ground elevation on a regular grid inside the site boundary.",
        points: ["CSV template provided", "Elevation sampling on Junior and Enterprise"],
      },
      {
        title: "Data health and QA/QC",
        body: "Imported data is checked and problems are listed as blockers or warnings. Standards, blanks and duplicates have their own review.",
        points: ["Missing collars or surveys, overlapping or empty intervals", "Control charts for standards", "Duplicate precision plots and a summary by batch"],
      },
    ],
    limits: [
      "Local mine grids and custom projection definitions are not supported.",
      "Imports are CSV only; Excel files are not accepted.",
      "MineOptics does not produce a mineral resource estimate. A block model can be imported, or estimated by one of two simple methods; kriging, variogram modelling and classification are not available.",
    ],
    docsPath: "/user-guide/site-management",
    plan: "All plans",
  },
  {
    slug: "block-models",
    name: "Block models & pit shell",
    title: "Find the pit in the model.",
    lede: "Bring a block model in, or estimate a simple one from the site's assays. Read its grade-tonnage curve, then work out the pit that pays at a scenario's prices and costs.",
    description:
      "Block models in MineOptics: import a model or estimate one by inverse distance, grade-tonnage curves, a 3D view, and a scoping-level pit shell with a yearly schedule.",
    blocks: [
      {
        title: "Import",
        body: "Import a block model that was estimated in another package, as a CSV with one row per block: the block centre, a density and a column for each grade.",
        points: ["Regular, unrotated grids of up to 250,000 blocks", "The unit of the grades is recorded with the model", "Shown in 3D, coloured by grade above a cut-off you set"],
      },
      {
        title: "Estimation",
        body: "Without a model of your own, estimate one from the site's assays: composites, a search, and one of two methods.",
        points: ["Inverse distance weighting or nearest neighbour", "A top cut applied before compositing", "Checks of the estimate against the composites, with a declustered mean"],
      },
      {
        title: "Statistics and grade-tonnage",
        body: "Describe the assays, then see what a model holds at each cut-off.",
        points: ["Assay and composite statistics, with an experimental variogram", "Tonnes and mean grade at or above each of 21 cut-offs", "A count of the model, not a resource statement"],
      },
      {
        title: "Pit shell",
        body: "For a scenario, work out which blocks are worth mining at its prices, recoveries and costs, with one wall slope.",
        points: ["Ore, waste, strip ratio and the undiscounted value of the pit", "Bench by bench, and drawn in 3D", "Says when prices or costs have changed since it was worked out"],
      },
      {
        title: "Yearly schedule",
        body: "The pit becomes a year-by-year schedule of ore, waste and grade, mined from the top bench down, which can be the scenario's mine plan.",
        points: ["Ore per year from the scenario's production target", "A mining capacity you set, or one that is assumed and labelled as assumed", "The projection and the fleet model are both costed on it"],
      },
    ],
    limits: [
      "This is not a mineral resource estimate. Estimation is by inverse distance or nearest neighbour only: no kriging, no variogram model, no estimation domains and no classification.",
      "The pit shell is a scoping-level result with one slope for every wall. It has no ramps, no minimum mining width and no pushbacks. It is not a pit design, and it is not a reserve.",
      "The schedule mines bench by bench from the top down. It is not an optimized schedule.",
      "The block model and pit shell calculations have not been reviewed against a published study by a mining engineer.",
    ],
    docsPath: "/user-guide/resource-modelling",
    plan: "Junior and Enterprise",
  },
  {
    slug: "drill-blast",
    name: "Drill & blast",
    title: "Compare drills on cost per tonne.",
    lede: "Design the blast pattern for a bench and size the drill fleet for every option you are considering. Results update as you change the inputs.",
    description:
      "Drill and blast analysis in MineOptics: blast geometry, drill fleet sizing and drilling cost per tonne for each drill option, with published formulas.",
    blocks: [
      {
        title: "Setup",
        body: "Annual production and operating hours start from the values stored on the site. Rock density and hardness come from the site too.",
        points: ["Annual production, Mt per year", "Operating hours per year", "Bench height, m"],
      },
      {
        title: "Explosives",
        body: "Set the loading assumptions that drive the pattern.",
        points: ["Powder factor, kg/t", "Subdrill, m", "Spacing ratio (spacing over burden)", "Explosive type as a label: emulsion, ANFO or heavy ANFO"],
      },
      {
        title: "Drill fleet",
        body: "Add drills from the preset list or define your own. Presets are generic starting points, meant to be replaced with your rates.",
        points: ["Hole diameter, mm", "Capital cost per rig and operating cost per hour", "Drill rate, utilization and amortization period"],
      },
      {
        title: "Results, drill by drill",
        body: "Every option is laid out next to the others, and the one with the lowest drilling cost per tonne is marked.",
        points: [
          "Burden, spacing and pattern",
          "Stiffness ratio and rating",
          "Volume and tonnes per hole",
          "Holes, metres and drill hours per year",
          "Rigs required",
          "Explosives per hole and per year",
          "Annual capital and operating cost",
          "Drilling cost per tonne",
        ],
      },
    ],
    limits: [
      "Drilling cost per tonne covers the drill rigs. It does not include the cost of explosives.",
      "The explosive type is a label and does not change the calculation.",
      "In the scenario projection, blasting is priced with generic explosive and crew rates until your own are entered, and the result says so.",
    ],
    docsPath: "/tutorials/compare-drill-options",
    plan: "All plans",
  },
  {
    slug: "cost-model",
    name: "Cost model",
    title: "One scenario for the whole operation.",
    lede: "A scenario describes a mining operation for a site. Keep a base case, duplicate it, change the mine plan, the fleet or the discount rate, and calculate again.",
    description:
      "Build open-pit cost model scenarios in MineOptics: a mine plan, equipment, processing, support services, revenue and lifecycle assumptions for each site, calculated into a cashflow.",
    blocks: [
      {
        title: "Scenarios",
        body: "A site can have any number of scenarios, one of them the base case. Duplicating one copies all of its configuration, with its fleet and its pit shell. The page address contains the site and scenario, so a colleague can open exactly what you are looking at.",
        points: ["Create, rename, duplicate and delete", "Each section shows whether it is not started, needs attention or is ready", "Viewers can read; operators and above can edit"],
      },
      {
        title: "Mine plan",
        body: "A scenario's tonnes come from one place, and you choose which. The projection and the fleet model are both costed on it, so the two cannot disagree.",
        points: [
          "A constant rate, with a ramp-up and a mine life",
          "The yearly schedule of a pit shell worked out in the site's block model",
          "A schedule imported from a scheduling package, by month, quarter or year",
        ],
      },
      {
        title: "Mobile equipment",
        body: "Cost loading and hauling one of two ways, scenario by scenario. The quick estimate sizes loaders and trucks from cycle times and charges them by the hour. The fleet model builds the fleet from the hours the plan needs.",
        points: [
          "Quick estimate: bucket and truck capacity, haul distance, speeds, availability and fuel",
          "Fleet model: machines by period, ageing, rebuilds, replacement and labour",
          "A fleet machine takes its price and rates from a vendor quote, and is capital in the year it is bought",
        ],
      },
      {
        title: "Processing",
        body: "Describe the plant stage by stage.",
        points: ["A concentrator or leach plant costed by area, each figure with its source", "Or a crushing plant built up from crushers, screens and conveyors", "A recovery for each metal"],
      },
      {
        title: "Support services",
        body: "Capture the people and fixed costs around the operation.",
        points: ["Management, technical and administrative positions with head count and salary", "Insurance, environmental, safety, communications and utilities", "Overhead and G&A percentages"],
      },
      {
        title: "Revenue and lifecycle",
        body: "Set the commercial terms and the shape of the project over time.",
        points: [
          "Grade, price and payable percentage for each commodity",
          "Treatment, refining and freight charges, royalty",
          "Construction period, mine life, ramp-up and closure",
          "Capital as an estimate: direct costs by area, with indirects, EPCM, the owner's costs, contingency and sustaining capital on them, or as a schedule you type",
          "One tax rate, or a tax model with a mining tax and reclamation security",
        ],
      },
      {
        title: "The projection",
        body: "Calculate, and the scenario's saved inputs become a year-by-year cashflow. Whatever could not be costed is listed next to the result.",
        points: [
          "NPV and IRR before and after tax, payback, peak funding",
          "Operating cost per tonne milled by cost centre",
          "Ore, waste and mill feed after ore loss and dilution",
          "Amounts in the currency the project states",
        ],
      },
      {
        title: "Sensitivity, comparison and export",
        body: "See what moves the result, set scenarios side by side, and take the numbers with you.",
        points: ["NPV against price, grade, recovery, operating cost, capital and strip ratio", "Every scenario of a site in one table", "Excel workbook with the warnings, assumptions and inputs; print to PDF"],
      },
    ],
    limits: [
      "The projection is a scoping-level model in constant terms: no inflation or escalation, and tax as one rate or a simplified tax model. It is not a substitute for a study signed by a Qualified Person.",
      "The projection and the fleet model have not been reviewed against a published study by a mining engineer.",
      "Nothing is converted between currencies. A project states the currency its amounts are in.",
      "Results are recalculated when you select Calculate, not on every change.",
    ],
    docsPath: "/user-guide/cost-model",
    plan: "Scenarios on all plans; sections vary by plan",
  },
  {
    slug: "reports",
    name: "Reports & history",
    title: "Issue the number. Keep the record.",
    lede: "When a scenario says what you mean it to say, issue it. A report is a numbered revision, frozen with everything the result rests on, that you can print, hand over and come back to.",
    description:
      "Issued reports in MineOptics: freeze a scenario as a numbered revision with its inputs, warnings and assumptions, compare two revisions, and keep a history of every change.",
    blocks: [
      {
        title: "Issuing a report",
        body: "From a scenario's results, issue a report with a title, who it is prepared for and a note. The projection is calculated again from the saved inputs at that moment and stored with them.",
        points: ["A revision number that is never used twice", "Names the project, the client, the issuing organization and the author", "States the currency of its amounts"],
      },
      {
        title: "What is frozen",
        body: "Everything the result rests on is kept with it, so a later change to the scenario does not reach a report that was issued.",
        points: [
          "The saved inputs of every section",
          "The imported schedule, the pit shell, the fleet and the vendor quotes it names",
          "The rock properties of the site",
          "The warnings and the conventions of the calculation",
        ],
      },
      {
        title: "The document",
        body: "A report opens as a document that reads the same whenever it is opened: the result, what it does not include, the basis of the estimate, the mine plan, operating cost, capital, cashflow by year and sensitivity with their charts, how it was calculated, and every input under its name with its unit. Your firm's logo is on the cover.",
        points: ["Prints, or saves as a PDF, from the browser", "Downloads as an Excel workbook", "Says on its cover that it is a scoping-level estimate"],
      },
      {
        title: "For its reader",
        body: "A link opens one issued report for someone outside your organization, without an account: the client, a reviewer, a lender. It opens that report and nothing else.",
        points: ["Works for 7, 30 or 90 days, and can be withdrawn at any time", "You see whether and when it was opened", "Only a fingerprint of the link is kept: it is shown once, to whoever makes it"],
      },
      {
        title: "Comparing revisions",
        body: "Open a report and compare it with another revision of the same scenario. Nothing is recalculated: both are read as they were issued.",
        points: ["Every headline result side by side, with the change", "Each input that differs, under the section it is entered in", "The warnings one revision has and the other does not"],
      },
      {
        title: "Scenario history",
        body: "Every save, calculation, issued report and copy of a scenario is recorded with who did it, when, and the values that changed.",
        points: ["Recorded by the server for every way of saving, the API included", "Any member can read it; no member can edit or delete an entry"],
      },
    ],
    limits: [
      "A report is a scoping-level study summary. It has not been reviewed by a Qualified Person and is not a technical report, a resource or reserve estimate, or a basis for an investment decision. Its cover says so.",
      "The PDF is made by your browser's print dialog. MineOptics does not produce the file itself.",
      "A link is a secret anyone can use: whoever it is forwarded to can read the report until it expires or you withdraw it. A reviewer who signs in without a paid seat is not built yet.",
      "The history covers scenarios. Imports of drillholes and block models are not recorded in it yet.",
    ],
    docsPath: "/user-guide/cost-model#reports",
    plan: "Issuing and sharing on Junior and Enterprise, and during the trial; reading on all plans",
  },
  {
    slug: "operations",
    name: "Operations",
    title: "Hold the mine to its plan.",
    lede: "Once a site is producing, record what happened each month and see where it stands against the plan it was built on: tonnes, grade, cost and capital, by month and to date.",
    description:
      "Plan against actual in MineOptics: monthly tonnes, head grades, operating cost, capital and revenue for a site, held against an issued report as the plan.",
    blocks: [
      {
        title: "The plan",
        body: "The plan is an issued report, so it does not move when someone edits the scenario. Choose the report and the month production started.",
        points: ["Reissue the scenario for a new revision, and choose that as the plan instead", "A report that is a site's plan cannot be deleted from under it"],
      },
      {
        title: "Months of actuals",
        body: "Enter a month in a form, or import many at once from a CSV.",
        points: [
          "Ore and waste mined, mill feed, head grades, operating cost, capital and net revenue",
          "A value left empty is not reported, which is not zero",
          "The import shows how each column was read, and refuses what it cannot be sure of",
        ],
      },
      {
        title: "The comparison",
        body: "Each measure is held against the plan for the months that reported it.",
        points: ["To date, by plan year and month by month", "Strip ratio, and operating cost and net revenue per tonne milled", "Head grades weighted by the tonnes milled"],
      },
    ],
    limits: [
      "It compares monthly totals that someone enters or imports. Nothing is read from equipment, a fleet management system or a plant.",
      "The plan is yearly and is spread evenly over its months, so a year that ramps up reads behind early and ahead late.",
      "Recovery and metal produced, cost by area and equipment hours are not compared yet, and there is no reforecast.",
      "Nothing is converted or escalated: the plan is in the currency and the money of the study.",
    ],
    docsPath: "/user-guide/operations",
    plan: "Junior and Enterprise",
  },
  {
    slug: "vendors-quotes",
    name: "Vendors & quotes",
    title: "Quotes that feed the model.",
    lede: "One register of suppliers, the equipment they offer and the quotes you have received, shared by every project in the organization.",
    description:
      "Keep a register of mining equipment vendors, models and quotes in MineOptics, and use quote rates to fill cost model inputs.",
    blocks: [
      {
        title: "Vendors",
        body: "Each supplier has a type, contact details and the equipment categories and regions it covers.",
        points: ["OEM, dealer, distributor or contractor", "Names unique within your organization"],
      },
      {
        title: "Equipment catalog",
        body: "Models belong to a vendor and carry a class, manufacturer, model name and specifications such as payload or throughput.",
        points: ["Haul trucks, loaders, drills, crushers and more", "Variants of the same model kept apart"],
      },
      {
        title: "Quotes",
        body: "A quote records what was offered, when, and until when. Two totals are calculated every time it is saved.",
        points: [
          "Capital per unit: base price, options, delivery, installation, commissioning, spare parts",
          "Operating cost per hour: maintenance, tyres, ground engaging tools, lubricants, consumables",
          "Shown as expiring in the 14 days before its valid-until date, and expired after it",
        ],
      },
      {
        title: "Into the scenario",
        body: "In a scenario's quick equipment estimate, pick a quote for the loader or the truck. Its type, hourly cost, fuel consumption and unit count are copied into the form, where you can still edit them. In the fleet model, each activity names a quote as its machine and takes its price, life, availability and rates from it.",
        points: ["A quote assigned to a scenario cannot be deleted by accident", "An issued report keeps the quotes its fleet was costed from, as they were"],
      },
    ],
    limits: [
      "Fuel is not included in a quote's operating cost per hour total.",
      "Assigning quotes to scenario equipment roles is done through the API; the dashboard does not have a screen for it yet.",
    ],
    docsPath: "/user-guide/vendors-quotes",
    plan: "Junior and Enterprise",
  },
];

/** Old feature URLs, kept alive as redirects. */
export const LEGACY_FEATURE_REDIRECTS: Record<string, string> = {
  drilling: "/features/drill-blast",
  blasting: "/features/drill-blast",
  revenue: "/features/cost-model",
  cashflow: "/features/cost-model",
  reporting: "/features/reports",
};
